import * as React from 'react';
import { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import {
    BadgeLabel,
    BodyMediumRegular,
    DownloadIcon,
    isWeb,
    PdfSheet,
    Row,
    RowList,
    SelectDropdown,
    Spinner,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { isoDateToLocalTimezoneDate } from '../../../../common/utils/dateUtils';
import { captureException } from '../../../../logger/Logger';
import { downloadCombinedStatement } from '../../../api/StatementApi';
import type { CombinedStatement } from '../../../types/Statement';

interface StatementsProps {
    statements: CombinedStatement[];
    userId?: string;
    downloadStatement?: (userId: string, yearMonth: string) => Promise<string>;
}

const Statements = ({
    statements,
    userId,
    downloadStatement = downloadCombinedStatement,
}: StatementsProps) => {
    const user = useAuthedUser();
    const statementUserId = userId ?? user.id;

    const [yearOptions, setYearOptions] = useState<string[]>([]);
    const [selectedYearOption, setSelectedYearOption] = useState<string | null>(
        null,
    );
    const [filteredStatements, setFilteredStatements] = useState<
        CombinedStatement[]
    >([]);

    const [fileInfo, setFileInfo] = useState<{
        uri: string;
        name: string;
    } | null>(null);
    const [isPdfSheetOpen, setIsPdfSheetOpen] = useState(false);

    useEffect(() => {
        const yearOptions = getYearOptionsFromStatements(statements);
        setYearOptions(yearOptions);

        if (selectedYearOption == null && yearOptions[0] != null) {
            setSelectedYearOption(yearOptions[0]);
        }
    }, [JSON.stringify(statements)]);

    useEffect(() => {
        setFilteredStatements(
            statements.filter(
                (statement) =>
                    isoDateToLocalTimezoneDate(statement.yearMonth)
                        .getFullYear()
                        .toString() === selectedYearOption,
            ),
        );
    }, [selectedYearOption]);

    const onDownloadStatement = async (yearMonth: string) => {
        const uri = await downloadStatement(statementUserId, yearMonth);

        setFileInfo({ uri, name: getFormattedYearMonth(yearMonth, true) });
        setIsPdfSheetOpen(true);
    };

    return (
        <YStack flex={1} marginHorizontal={8}>
            {isEmpty(statements) ? (
                <BodyMediumRegular>
                    You don&apos;t have any statements.
                </BodyMediumRegular>
            ) : (
                <YStack flex={1}>
                    <View marginRight={-12} marginLeft={-10} marginBottom={12}>
                        <SelectDropdown
                            defaultValue={selectedYearOption as string}
                            options={yearOptions}
                            onSelect={(selectedYear, _) => {
                                setSelectedYearOption(selectedYear);
                            }}
                        />
                    </View>
                    <ScrollView
                        style={{ flex: 1 }}
                        showsVerticalScrollIndicator={isWeb}
                    >
                        <RowList>
                            {filteredStatements.map((doc) => (
                                <StatementRow
                                    key={doc.yearMonth}
                                    statement={doc}
                                    onPressDownload={onDownloadStatement}
                                />
                            ))}
                        </RowList>
                    </ScrollView>
                </YStack>
            )}
            {fileInfo?.uri != null && (
                <PdfSheet
                    fileUri={fileInfo.uri}
                    fileName={fileInfo.name}
                    isOpen={isPdfSheetOpen}
                    onClose={() => {
                        setIsPdfSheetOpen(false);
                    }}
                />
            )}
        </YStack>
    );
};

interface StatementRowProps {
    statement: CombinedStatement;
    onPressDownload: (yearMonth: string) => Promise<void>;
}

const StatementRow = ({ statement, onPressDownload }: StatementRowProps) => {
    const toast = useToastController();

    const { yearMonth } = statement;
    const [isDownloading, setIsDownloading] = useState(false);

    const onPress = async () => {
        setIsDownloading(true);
        try {
            await onPressDownload(yearMonth);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Row
            title={getFormattedYearMonth(yearMonth)}
            onPress={onPress}
            rightContent={
                isDownloading ? (
                    <Spinner />
                ) : (
                    <BadgeLabel
                        variant="neutral"
                        text="Download"
                        icon={DownloadIcon}
                    />
                )
            }
        />
    );
};

const getYearOptionsFromStatements = (
    statements?: CombinedStatement[] | null,
): string[] => {
    if (statements == null) return [];

    const years = statements.reduce((years: string[], statement) => {
        const { yearMonth } = statement;
        const year = isoDateToLocalTimezoneDate(yearMonth)
            .getFullYear()
            .toString();
        if (!years.includes(year)) years.push(year);
        return years;
    }, []);

    return years.sort((a, b) => +b - +a);
};

const getFormattedYearMonth = (yearMonth: string, includeYear = false) =>
    isoDateToLocalTimezoneDate(yearMonth).toLocaleString('default', {
        month: 'long',
        ...(includeYear ? { year: 'numeric' } : {}),
    });

export default Statements;
